import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Button,
  Alert,
  Tag,
  Typography,
  Spin,
  Divider,
} from "antd";
import { useNavigate } from "react-router";
import RoleSelect from "@/components/Admin/RoleSelect";
import { useQuery } from "@tanstack/react-query";
import { useUser } from "@/contexts/UserContext";
import { useImpersonation } from "@/contexts/useSuperuser";
import { superuserService } from "@/services/superuserService";
import type { ImpersonatableUser } from "@/contexts/superuser.types";

const { Title, Text } = Typography;
const { Search } = Input;

export default function SuperuserPanel() {
  const { userInfo } = useUser();
  const navigate = useNavigate();
  const {
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
    impersonatedRole,
    startRoleImpersonation,
    stopRoleImpersonation,
  } = useImpersonation();

  const [searchValue, setSearchValue] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | undefined>(
    undefined,
  );
  const [roleLoading, setRoleLoading] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);

  useEffect(() => {
    if (userInfo && !userInfo.is_superuser) {
      navigate(-1);
    }
  }, [userInfo, navigate]);

  const {
    data: allUsers = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["superuser-users"],
    queryFn: superuserService.fetchImpersonatableUsers,
  });

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ["impersonatable-roles"],
    queryFn: superuserService.fetchImpersonatableRoles,
  });

  const handleStartRole = useCallback(async () => {
    if (!selectedRole) return;
    setRoleLoading(true);
    setRoleError(null);
    try {
      await startRoleImpersonation(selectedRole);
    } catch (e: unknown) {
      setRoleError(
        e instanceof Error ? e.message : "Role impersonation failed",
      );
    } finally {
      setRoleLoading(false);
    }
  }, [selectedRole, startRoleImpersonation]);

  const handleStopRole = useCallback(async () => {
    setRoleLoading(true);
    setRoleError(null);
    try {
      await stopRoleImpersonation();
      setSelectedRole(undefined);
    } catch (e: unknown) {
      setRoleError(
        e instanceof Error ? e.message : "Failed to exit role impersonation",
      );
    } finally {
      setRoleLoading(false);
    }
  }, [stopRoleImpersonation]);

  const filteredUsers = useMemo(() => {
    const searchTerm = searchValue.toLowerCase();
    if (!searchTerm) return allUsers;
    return allUsers.filter(
      (u) =>
        u.email.toLowerCase().includes(searchTerm) ||
        u.first_name.toLowerCase().includes(searchTerm) ||
        u.last_name.toLowerCase().includes(searchTerm) ||
        (u.company ?? "").toLowerCase().includes(searchTerm),
    );
  }, [allUsers, searchValue]);

  const handleImpersonate = useCallback(
    async (userId: string) => {
      setActionLoading(userId);
      setActionError(null);
      try {
        await startImpersonation(userId);
      } catch (e: unknown) {
        setActionError(e instanceof Error ? e.message : "Impersonation failed");
      } finally {
        setActionLoading(null);
      }
    },
    [startImpersonation],
  );

  const handleStop = useCallback(async () => {
    setActionLoading("stop");
    setActionError(null);
    try {
      await stopImpersonation();
    } catch (e: unknown) {
      setActionError(
        e instanceof Error ? e.message : "Failed to stop impersonation",
      );
    } finally {
      setActionLoading(null);
    }
  }, [stopImpersonation]);

  const columns = useMemo(
    () => [
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
      },
      {
        title: "Name",
        key: "name",
        render: (_: unknown, u: ImpersonatableUser) =>
          [u.first_name, u.last_name].filter(Boolean).join(" ") || "—",
      },
      {
        title: "Company",
        dataIndex: "company",
        key: "company",
        render: (v: string) => v || "—",
      },
      {
        title: "",
        key: "action",
        width: 140,
        render: (_: unknown, u: ImpersonatableUser) => {
          const isActive = impersonatedUser?.id === u.id;
          const isOtherActive =
            !!impersonatedUser && impersonatedUser.id !== u.id;
          return (
            <Button
              size="small"
              type={isActive ? "default" : "primary"}
              danger={isActive}
              loading={actionLoading === u.id}
              disabled={isOtherActive || actionLoading === "stop"}
              onClick={() =>
                isActive ? handleStop() : handleImpersonate(u.id)
              }
            >
              {isActive ? "Stop" : "Impersonate"}
            </Button>
          );
        },
      },
    ],
    [impersonatedUser, actionLoading, handleStop, handleImpersonate],
  );

  return (
    <div className="w-full px-10 py-8">
      <Title level={3} className="!mb-1">
        Superuser Panel
      </Title>
      <Text type="secondary" className="block mb-6">
        Impersonate any active user to inspect their session. An orange banner
        appears at the top of every page while impersonation is active. Clicking
        &ldquo;Stop&rdquo; here or in the banner restores your session.
      </Text>

      {impersonatedUser && (
        <Alert
          type="warning"
          showIcon
          className="mb-4"
          title={
            <span>
              Impersonating <strong>{impersonatedUser.email}</strong>{" "}
              <Tag color="orange">{impersonatedUser.name}</Tag>
            </span>
          }
          action={
            <Button
              size="small"
              loading={actionLoading === "stop"}
              onClick={handleStop}
            >
              Stop Impersonating
            </Button>
          }
        />
      )}

      <div className="mb-6 rounded-md border border-indigo-200 bg-indigo-50/50 p-4">
        <Title level={5} className="!mb-1">
          Role Impersonation
        </Title>
        <Text type="secondary" className="block mb-3">
          Act as if you hold a specific role across all trials, without changing
          your identity. Useful for QA and permission testing. An indigo banner
          appears while a role is active.
        </Text>

        {roleError && (
          <Alert
            type="error"
            title={roleError}
            className="mb-3"
            closable={{ onClose: () => setRoleError(null) }}
          />
        )}

        {impersonatedRole ? (
          <div className="flex items-center gap-3 flex-wrap">
            <span>
              Currently viewing as role{" "}
              <Tag color="purple">{impersonatedRole}</Tag>
            </span>
            <Button danger loading={roleLoading} onClick={handleStopRole}>
              Exit Role Impersonation
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <RoleSelect
              placeholder="Select a role…"
              className="min-w-[220px]"
              loading={rolesLoading}
              value={selectedRole}
              onChange={setSelectedRole}
              disabled={!!impersonatedUser}
              options={roles.map((r) => ({
                value: r.slug,
                label: r.name ? `${r.name} (${r.slug})` : r.slug,
              }))}
            />
            <Button
              type="primary"
              loading={roleLoading}
              disabled={!selectedRole || !!impersonatedUser}
              onClick={handleStartRole}
            >
              Impersonate Role
            </Button>
            {impersonatedUser && (
              <Text type="secondary">
                Stop user impersonation before impersonating a role.
              </Text>
            )}
          </div>
        )}
      </div>

      <Divider />

      {isError && (
        <Alert type="error" title="Failed to load users." className="mb-4" />
      )}

      {actionError && (
        <Alert
          type="error"
          title={actionError}
          className="mb-4"
          closable={{ onClose: () => setActionError(null) }}
        />
      )}

      {isLoading ? (
        <Spin className="block mt-12" />
      ) : (
        <>
          <Search
            placeholder="Search by email, name or company…"
            allowClear
            onChange={(e) => setSearchValue(e.target.value)}
            className="mb-4 max-w-md"
          />
          <Table
            dataSource={filteredUsers}
            columns={columns}
            rowKey="id"
            size="middle"
            pagination={{ pageSize: 20, hideOnSinglePage: true }}
            rowClassName={(u) =>
              impersonatedUser?.id === u.id ? "bg-orange-50" : ""
            }
          />
        </>
      )}
    </div>
  );
}
